/** Draft model + defaults for the K8s cluster-create wizard. Mirrors the legacy
 *  `cluster:{...}` payload (basic + network + multi-pool + 8 add-ons). */

export interface PoolDraft {
  name: string;
  type: string;
  flavor: string;
  enableGpu: boolean;
  gpu: { type: string; count: number };
  min: number;
  max: number;
  volumes: number;
  autoscaling: boolean;
}

export interface ClusterTag {
  key: string;
  value: string;
}

export interface ClusterDraft {
  /** Customer-facing personal label — free-text, editable. */
  name: string;
  /** System-managed identifier (k8s-c-<slug>) — auto-generated, not customer-editable.
   *  Decoupled from name; used by the backend for the cluster fqdn + worker/pool naming. */
  uid: string;
  description: string;
  version: string;
  runtime: string;
  resource_group: string;
  network: { cni: string; subnet: string; pod_cidr: string; service_cidr: string };
  pools: PoolDraft[];
  addons: Record<string, Record<string, unknown>>;
  tags: ClusterTag[];
}

// Values MUST match the backend `flavor.type` strings — the flavor select filters
// `flavor.type === pool.type`. Legacy @console uses these exact slugs; the short
// forms (`processor`/`memory`) match nothing → empty flavor list on type change.
export const POOL_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'processor-optimized', label: 'Processor Optimized' },
  { value: 'memory-optimized', label: 'Memory Optimized' },
];

/** Short lowercase alphanumeric slug for default cluster/nodepool names (mirrors the
 *  legacy `@console`, which defaults both rather than asking for free-text). */
export function randomSlug(len = 6): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < len; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return s;
}

export function newPool(_idx: number): PoolDraft {
  return {
    // Auto-generated slug — the nodepool name is derived (<clusterName>-<slug>), not
    // free-text, matching @console where the field is read-only.
    name: randomSlug(),
    type: 'standard',
    flavor: '',
    enableGpu: false,
    gpu: { type: '', count: 1 },
    min: 1,
    max: 3,
    volumes: 30,
    autoscaling: false,
  };
}

export function defaultDraft(): ClusterDraft {
  // name is pre-filled with the same k8s-c-<slug> as the uid (mirrors @console) but
  // stays editable; uid is the system identifier the backend manages (not editable).
  // The server-suggested slug overrides both once the config endpoint loads.
  const slug = `k8s-c-${randomSlug()}`;
  return {
    name: slug,
    uid: slug,
    description: '',
    version: '',
    runtime: 'containerd',
    resource_group: '',
    network: { cni: 'calico', subnet: '', pod_cidr: '10.244.0.0/16', service_cidr: '10.96.0.0/12' },
    pools: [newPool(0)],
    // Default-enabled add-ons — same literal payload @console seeds (NGINX Ingress,
    // Metrics Server, Block Storage). Inlined (not derived from ADDON_CATALOG) so the
    // seed can't depend on module init order and matches the known-good shape exactly.
    addons: {
      nginx_ingress: { enabled: true, type: 'LoadBalancer', class_name: 'nginx' },
      metrics_server: { enabled: true },
      storage_provisioner: { enabled: true, type: 'bs', default_class: true },
    },
    tags: [],
  };
}

/** Static add-on catalog (the config endpoint doesn't return these). Each entry
 *  declares its configurable fields shown when enabled. */
export interface AddonField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'switch' | 'select';
  placeholder?: string;
  options?: { value: string; label: string }[];
  /** Pull options at render time from runtime data (e.g. the NFS flavor whitelist). */
  dynamicOptions?: 'nfsFlavors';
  default?: unknown;
  /** Helper/warning text rendered under the field. */
  note?: string;
  showIf?: (cfg: Record<string, unknown>) => boolean;
}

/** Flavors offered when provisioning a dedicated NFS server instance. */
export const NFS_FLAVOR_WHITELIST = ['v2g-standard-2-4', 'v2g-standard-4-4', 'v2g-memory-4-8'];
export interface AddonDef {
  key: string;
  label: string;
  description: string;
  fields: AddonField[];
}

const EXPOSE = [
  { value: 'ClusterIP', label: 'ClusterIP (internal)' },
  { value: 'LoadBalancer', label: 'LoadBalancer (public)' },
];

export const ADDON_CATALOG: AddonDef[] = [
  {
    key: 'nginx_ingress', label: 'NGINX Ingress', description: 'L7 ingress controller for HTTP/HTTPS routing.',
    fields: [
      { key: 'type', label: 'Service type', type: 'select', options: EXPOSE, default: 'LoadBalancer' },
      { key: 'class_name', label: 'Ingress class', type: 'text', placeholder: 'nginx', default: 'nginx' },
    ],
  },
  { key: 'metrics_server', label: 'Metrics Server', description: 'Resource metrics for HPA and kubectl top.', fields: [] },
  {
    key: 'cert_manager', label: 'cert-manager', description: 'Automated TLS certificate management.',
    fields: [
      { key: 'email', label: 'ACME email', type: 'text', placeholder: 'admin@example.com' },
      { key: 'staging', label: 'Use staging issuer (untrusted test certificates)', type: 'switch', default: false },
    ],
  },
  {
    key: 'kubernetes_dashboard', label: 'Kubernetes Dashboard', description: 'Web UI for the cluster.',
    fields: [{ key: 'expose_type', label: 'Expose via', type: 'select', options: EXPOSE, default: 'ClusterIP' }],
  },
  {
    key: 'argocd', label: 'Argo CD', description: 'GitOps continuous delivery.',
    fields: [{ key: 'expose_type', label: 'Expose via', type: 'select', options: EXPOSE, default: 'ClusterIP' }],
  },
  {
    key: 'prometheus', label: 'Prometheus', description: 'Metrics collection and alerting.',
    fields: [
      { key: 'retention_days', label: 'Retention (days)', type: 'number', placeholder: '15', default: 15 },
      { key: 'storage_size', label: 'Storage (GiB)', type: 'number', placeholder: '20', default: 20 },
    ],
  },
  {
    key: 'grafana', label: 'Grafana', description: 'Dashboards for Prometheus metrics.',
    fields: [{ key: 'admin_password', label: 'Admin password', type: 'text', placeholder: 'auto-generated if empty' }],
  },
  {
    key: 'storage_provisioner', label: 'Storage Provisioner', description: 'Dynamic PersistentVolume provisioning.',
    fields: [
      { key: 'type', label: 'Backend', type: 'select', default: 'bs', options: [
        { value: 'bs', label: 'Block Storage' }, { value: 'nfs', label: 'NFS (Shared Storage)' }, { value: 'hostpath', label: 'HostPath (Node-local)' },
      ] },
      { key: 'default_class', label: 'Set as default StorageClass', type: 'switch', default: true },
      // HostPath
      {
        key: 'hostpath_storage_tier', label: 'HostPath tier', type: 'select', default: 'hybrid',
        options: [
          { value: 'hybrid', label: 'Hybrid (NVMe + HDD)' },
          { value: 'nvme', label: 'NVMe (fast)' },
          { value: 'hdd', label: 'HDD (standard)' },
        ],
        showIf: (c) => c.type === 'hostpath',
        note: 'csi-hostpath-fast: high-performance (NVMe/SSD) for databases & caches · csi-hostpath-slow: standard (HDD) for logs, backups & bulk data · volumes are node-local and cannot be accessed from other nodes.',
      },
      // NFS — source selector then conditional config
      {
        key: 'nfs_source', label: 'NFS source', type: 'select', default: 'existing',
        options: [
          { value: 'existing', label: 'Use existing NFS server' },
          { value: 'new', label: 'Create new NFS instance' },
        ],
        showIf: (c) => c.type === 'nfs',
      },
      {
        key: 'nfs_server', label: 'NFS server address', type: 'text', placeholder: 'nfs.example.com or 192.168.1.100',
        showIf: (c) => c.type === 'nfs' && (c.nfs_source ?? 'existing') === 'existing',
        note: 'NFS server must be reachable from the cluster network; firewall must allow NFS traffic (ports 111, 2049).',
      },
      {
        key: 'nfs_instance_flavor', label: 'NFS instance flavor', type: 'select', dynamicOptions: 'nfsFlavors',
        showIf: (c) => c.type === 'nfs' && c.nfs_source === 'new',
      },
      {
        key: 'nfs_instance_storage', label: 'NFS storage (GiB)', type: 'number', placeholder: '50', default: 50,
        showIf: (c) => c.type === 'nfs' && c.nfs_source === 'new',
        note: 'Minimum 50 GB, maximum 1000 GB. A dedicated NFS server instance will be created in the same network as your cluster.',
      },
    ],
  },
];

/** Build the API `addons` object: only enabled add-ons, with their config. */
export function buildAddons(addons: Record<string, Record<string, unknown>>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const def of ADDON_CATALOG) {
    const cfg = addons[def.key];
    if (!cfg?.enabled) continue;
    const payload = { ...cfg };
    // The backend provisions a dedicated NFS server only when `nfs_create_instance`
    // is truthy; the wizard tracks that intent via the `nfs_source` selector.
    if (def.key === 'storage_provisioner' && payload.type === 'nfs') {
      payload.nfs_create_instance = payload.nfs_source === 'new';
    }
    out[def.key] = payload;
  }
  return out;
}
